import React, { useState, useEffect } from 'react';
import { Card, Table, Spin, Empty } from 'antd';
import {
  TrendingUp,
  TrendingDown,
  IndianRupee,
  Percent,
} from 'lucide-react';
import { MetricCard, HeadlineCard } from '../../components/insights';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import useApiMethods from '../../lib/useApiMethods';
import { PL_STATEMENT } from '../../constants/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

const formatINR = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

const PLTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { fetch } = useApiMethods();

  useEffect(() => {
    const loadPL = async () => {
      const res = await fetch(PL_STATEMENT, {}, setLoading);
      if (res?.status !== 'fail') {
        setData(res?.data || res);
      }
    };
    loadPL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Empty description="No P&L data available" className="py-20" />;
  }

  const { summary = {}, monthly = [] } = data;

  const marginPercent = summary.total_revenue
    ? ((summary.net_profit / summary.total_revenue) * 100).toFixed(1)
    : 0;

  const chartData = {
    labels: monthly.map((m) => m.month),
    datasets: [
      {
        type: 'bar',
        label: 'Revenue',
        data: monthly.map((m) => m.revenue),
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
        borderColor: '#10b981',
        borderWidth: 1,
        borderRadius: 4,
        order: 2,
      },
      {
        type: 'bar',
        label: 'Expenses',
        data: monthly.map((m) => m.expenses),
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderColor: '#ef4444',
        borderWidth: 1,
        borderRadius: 4,
        order: 2,
      },
      {
        type: 'line',
        label: 'Net Profit',
        data: monthly.map((m) => m.net_profit),
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#6366f1',
        tension: 0.3,
        fill: false,
        order: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, padding: 20, color: 'rgba(148,163,184,0.9)' },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${formatINR(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: 'rgba(148,163,184,0.9)' } },
      y: {
        ticks: {
          callback: (value) => `₹${(value / 100000).toFixed(0)}L`,
          color: 'rgba(148,163,184,0.9)',
        },
        grid: { color: 'rgba(148,163,184,0.18)' },
      },
    },
  };

  const tableColumns = [
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      render: (text) => <span className="font-medium">{text}</span>,
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (val) => formatINR(val),
      align: 'right',
    },
    {
      title: 'Expenses',
      dataIndex: 'expenses',
      key: 'expenses',
      render: (val) => formatINR(val),
      align: 'right',
    },
    {
      title: 'Net Profit',
      dataIndex: 'net_profit',
      key: 'net_profit',
      render: (val) => (
        <span className={val >= 0 ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-red-600 font-medium'}>
          {formatINR(val)}
        </span>
      ),
      align: 'right',
    },
    {
      title: 'Margin %',
      dataIndex: 'margin',
      key: 'margin',
      render: (val) => (
        <span className={val >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600'}>
          {val?.toFixed(1)}%
        </span>
      ),
      align: 'right',
    },
  ];

  // Headline state based on profitability
  const profit = Number(summary.net_profit || 0);
  const marginNum = Number(marginPercent || 0);
  const headlineState = profit < 0 ? 'critical' : marginNum < 10 ? 'warning' : 'healthy';
  const headlineTitle = profit < 0
    ? `Running at a loss of ${formatINR(Math.abs(profit))} over last 6 months`
    : marginNum < 10
      ? `Thin margin at ${marginPercent}% — expense review recommended`
      : `Healthy ${marginPercent}% margin on ${formatINR(summary.total_revenue || 0)} revenue`;

  return (
    <div className="space-y-6">
      {/* Headline */}
      <HeadlineCard
        state={headlineState}
        title={headlineTitle}
        description={`Last 6 months rollup · ${monthly.length} months tracked`}
        metrics={[
          { label: 'Revenue', value: formatINR(summary.total_revenue || 0) },
          { label: 'Expenses', value: formatINR(summary.total_expenses || 0) },
          { label: 'Net Profit', value: formatINR(profit) },
          { label: 'Margin', value: `${marginPercent}%` },
        ]}
      />

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={TrendingUp}
          intent="positive"
          title="Total Revenue"
          value={formatINR(summary.total_revenue || 0)}
          sparkline={monthly.map((m) => m.revenue || 0)}
          goodUp={true}
        />
        <MetricCard
          icon={TrendingDown}
          intent="negative"
          title="Total Expenses"
          value={formatINR(summary.total_expenses || 0)}
          sparkline={monthly.map((m) => m.expenses || 0)}
          goodUp={false}
        />
        <MetricCard
          icon={IndianRupee}
          intent={profit >= 0 ? 'positive' : 'negative'}
          title="Net Profit"
          value={formatINR(profit)}
          sparkline={monthly.map((m) => m.net_profit || 0)}
          goodUp={true}
        />
        <MetricCard
          icon={Percent}
          intent={marginNum >= 20 ? 'positive' : marginNum >= 10 ? 'warning' : 'negative'}
          title="Margin %"
          value={`${marginPercent}%`}
          sparkline={monthly.map((m) => m.margin || 0)}
          goodUp={true}
        />
      </div>

      {/* Chart */}
      <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
        <h4 className="font-semibold text-foreground mb-4">Revenue vs Expenses (6 Months)</h4>
        <div style={{ height: 400 }}>
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* Monthly Breakdown Table */}
      <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
        <h4 className="font-semibold text-foreground mb-4">Monthly Breakdown</h4>
        <Table
          dataSource={monthly}
          columns={tableColumns}
          rowKey={(record) => record.month}
          pagination={false}
          locale={{ emptyText: <Empty description="No data" /> }}
        />
      </div>
    </div>
  );
};

export default PLTab;
